// WooNotesPopupMenu.tsx
import React from 'react'
import { ElementDimensions, useElementDimensions } from '../hooks/useElementDimensions'

const WOO = {
  dark: '#0E4A2C',
  text: '#0B2B18',
  sub: 'rgba(11, 43, 24, 0.65)',
  bg: '#F6FFFA',
  hover: 'rgba(184, 240, 107, 0.22)',
  active: 'rgba(184, 240, 107, 0.35)',
  sep: 'rgba(14, 74, 44, 0.14)',
  shadow: '0 8px 0 #0E4A2C, 0 18px 30px rgba(14, 74, 44, 0.18)',
  dangerBg: 'linear-gradient(180deg, #FFB4A2 0%, #FF8A6A 55%, #F65C4A 100%)',
  dangerText: '#3B0B05',
}

export type WooMenuItem =
  | {
      kind?: 'item'
      id: string
      label: string
      icon?: string
      subtitle?: string
      rightHint?: string
      danger?: boolean
      disabled?: boolean
      onSelect: () => void
    }
  | { kind: 'separator'; id: string }

type Anchor = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'

type Props = {
  open: boolean
  anchor?: Anchor
  title?: string
  items: WooMenuItem[]
  onClose?: () => void
  showCaret?: boolean
  caretOffsetPx?: number
  positionOverride?: { x: number; y: number }
}

const ANIM_MS = 150

function getPanelPos(anchor: Anchor, panelDimensions: ElementDimensions, positionOverride?: { x: number; y: number }): React.CSSProperties {
  console.log(`getPanelPos`, positionOverride)
  console.log(`panelDimensions.height`, panelDimensions.height)
  if (positionOverride) {
    if (anchor === 'top-left') {
      return { top: positionOverride.y - panelDimensions.height - 30, left: positionOverride.x - 32 }
    } else {
      return { top: positionOverride.y + 30, left: positionOverride.x - 32 }
    }
  }

  switch (anchor) {
    case 'bottom-left':
      return { top: 'calc(100% + 12px)', left: 0 }
    case 'top-right':
      return { bottom: 'calc(100% + 12px)', right: 0 }
    case 'top-left':
      return { bottom: 'calc(100% + 12px)', left: 0 }
    case 'bottom-right':
    default:
      return { top: 'calc(100% + 12px)', right: 0 }
  }
}

/**
 * Rigtig caret (trekant) med outline + fyld + lille shadow + bridge.
 */
function renderCaret(anchor: Anchor, offset: number) {
  const isBottom = anchor.startsWith('bottom')
  const isRight = anchor.endsWith('right')

  const outerW = 12
  const outerH = 14
  const innerW = 10
  const innerH = 12

  const x = 22 + offset
  const overlap = 3

  const pos: React.CSSProperties = {
    position: 'absolute',
    zIndex: 3,
    pointerEvents: 'none',
    ...(isBottom ? { top: -outerH + overlap } : { bottom: -outerH + overlap }),
    ...(isRight ? { right: x } : { left: x }),
    width: 0,
    height: 0,
  }

  const outer: React.CSSProperties = isBottom
    ? {
        borderLeft: `${outerW}px solid transparent`,
        borderRight: `${outerW}px solid transparent`,
        borderBottom: `${outerH}px solid ${WOO.dark}`,
      }
    : {
        borderLeft: `${outerW}px solid transparent`,
        borderRight: `${outerW}px solid transparent`,
        borderTop: `${outerH}px solid ${WOO.dark}`,
      }

  const inner: React.CSSProperties = {
    position: 'absolute',
    left: -innerW,
    width: 0,
    height: 0,
    ...(isBottom
      ? {
          top: 2,
          borderLeft: `${innerW}px solid transparent`,
          borderRight: `${innerW}px solid transparent`,
          borderBottom: `${innerH}px solid ${WOO.bg}`,
        }
      : {
          bottom: 2,
          borderLeft: `${innerW}px solid transparent`,
          borderRight: `${innerW}px solid transparent`,
          borderTop: `${innerH}px solid ${WOO.bg}`,
        }),
  }

  const shadow: React.CSSProperties = {
    position: 'absolute',
    left: -outerW,
    width: 0,
    height: 0,
    opacity: 0.35,
    transform: 'translateY(3px)',
    zIndex: -1,
    ...(isBottom
      ? {
          top: 0,
          borderLeft: `${outerW}px solid transparent`,
          borderRight: `${outerW}px solid transparent`,
          borderBottom: `${outerH}px solid ${WOO.dark}`,
        }
      : {
          bottom: 0,
          borderLeft: `${outerW}px solid transparent`,
          borderRight: `${outerW}px solid transparent`,
          borderTop: `${outerH}px solid ${WOO.dark}`,
        }),
  }

  const bridge: React.CSSProperties = {
    position: 'absolute',
    zIndex: 4,
    pointerEvents: 'none',
    background: WOO.bg,
    width: outerW * 2 + 10,
    height: 6,
    ...(isBottom ? { top: 0 } : { bottom: 0 }),
    ...(isRight ? { right: x - 2 } : { left: x - 2 }),
  }

  return (
    <>
      <div style={bridge} />
      <div style={{ ...pos, ...outer }}>
        <div style={shadow} />
        <div style={inner} />
      </div>
    </>
  )
}

function WooMenuButton({ it, onClose }: { it: Exclude<WooMenuItem, { kind: 'separator' }>; onClose: () => void }) {
  const [hovered, setHovered] = React.useState(false)
  const [pressed, setPressed] = React.useState(false)

  const disabled = !!it.disabled
  const danger = !!it.danger

  const baseBg = danger ? WOO.dangerBg : 'rgba(255,255,255,0.65)'
  const hoverBg = danger ? WOO.dangerBg : WOO.hover
  const activeBg = danger ? WOO.dangerBg : WOO.active

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        if (disabled) return
        it.onSelect()
        onClose()
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false)
        setPressed(false)
      }}
      onMouseDown={() => {
        if (disabled) return
        setPressed(true)
      }}
      onMouseUp={() => {
        if (disabled) return
        setPressed(false)
      }}
      style={{
        width: '100%',
        textAlign: 'left',
        borderRadius: 14,
        border: `2px solid ${WOO.dark}`,
        padding: '10px 10px',
        cursor: disabled ? 'not-allowed' : 'pointer',

        background: pressed ? activeBg : hovered ? hoverBg : baseBg,
        color: danger ? WOO.dangerText : WOO.text,

        //boxShadow: pressed ? `0 3px 0 ${WOO.dark}` : `0 5px 0 ${WOO.dark}`,
        transform: pressed ? 'translateY(2px)' : 'translateY(0px)',
        opacity: disabled ? 0.55 : 1,

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,

        transition: 'transform 120ms ease, box-shadow 120ms ease, background 120ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {it.icon ? (
          <span
            aria-hidden
            style={{
              width: 28,
              height: 28,
              borderRadius: 10,
              border: danger ? `2px solid rgba(0,0,0,0.35)` : `2px solid ${WOO.dark}`,
              //backgroundColor: 'black',
              background: danger ? 'rgba(0,0,0,0.35)' : 'rgba(184, 240, 107, 0.45)', // blød brand-grøn
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: danger ? WOO.dangerText : WOO.text, // ikke hvid -> mindre “button”
              flex: '0 0 auto',
              fontSize: 14,
              lineHeight: '14px',
            }}
          >
            {it.icon}
          </span>
        ) : null}

        {/* Denne er en cirkel i stedet for */}
        {/* {it.icon ? (
          <span
            aria-hidden
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              border: `2px solid ${WOO.dark}`,
              background: 'transparent',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: danger ? WOO.dangerText : WOO.text,
              flex: '0 0 auto',
              fontSize: 14,
              lineHeight: '14px',
            }}
          >
            {it.icon}
          </span>
        ) : null} */}

        <div style={{ minWidth: 0, display: 'grid', gap: 2 }}>
          <div
            style={{
              fontWeight: 900,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {it.label}
          </div>
          {it.subtitle ? (
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: danger ? 'rgba(59, 11, 5, 0.75)' : WOO.sub,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {it.subtitle}
            </div>
          ) : null}
        </div>
      </div>

      {it.rightHint ? (
        <span
          style={{
            fontSize: 12,
            fontWeight: 900,
            padding: '6px 10px',
            borderRadius: 12,
            border: `2px solid ${WOO.dark}`,
            background: 'rgba(14, 74, 44, 0.06)',
            boxShadow: `0 4px 0 ${WOO.dark}`,
            color: danger ? WOO.dangerText : WOO.text,
            flex: '0 0 auto',
          }}
        >
          {it.rightHint}
        </span>
      ) : null}
    </button>
  )
}

export function WooNotesPopupMenu({
  open,
  anchor = 'bottom-right',
  title = 'Settings',
  items,
  onClose,
  showCaret = true,
  caretOffsetPx = 0,
  positionOverride,
}: Props) {
  // ✅ holder menuen renderet under fade-out
  const [rendered, setRendered] = React.useState(open)
  const [entered, setEntered] = React.useState(false)

  //const panelRef = React.useRef<HTMLDivElement | null>(null)
  const { ref: panelRef, dimensions: panelDimensions } = useElementDimensions<HTMLDivElement>([rendered])

  React.useEffect(() => {
    let raf = 0
    let t: number | undefined

    if (open) {
      setRendered(true)
      setEntered(false)
      raf = requestAnimationFrame(() => setEntered(true))
    } else {
      // start exit
      setEntered(false)
      t = window.setTimeout(() => setRendered(false), ANIM_MS)
    }

    return () => {
      if (raf) cancelAnimationFrame(raf)
      if (t) window.clearTimeout(t)
    }
  }, [open])

  React.useEffect(() => {
    // Kun når menu er åbent (ikke når den fader ud)
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.()
    }
    const onClick = (e: MouseEvent) => {
      console.log('popupmenu onclick')
      const el = panelRef.current
      if (!el) return
      if (e.target instanceof Node && !el.contains(e.target)) onClose?.()
    }

    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [open, onClose])

  if (!rendered) return null

  const transformOrigin = `${anchor.endsWith('right') ? 'right' : 'left'} ${anchor.startsWith('bottom') ? 'top' : 'bottom'}`

  const enterTranslateY = anchor.startsWith('bottom') ? -6 : 6

  return (
    <div
      ref={panelRef}
      role="menu"
      aria-label={title}
      style={{
        position: 'absolute',
        zIndex: 50,
        minWidth: 280,
        ...getPanelPos(anchor, panelDimensions, positionOverride),

        borderRadius: 18,
        border: `2px solid ${WOO.dark}`,
        background: WOO.bg,
        boxShadow: WOO.shadow,
        overflow: 'visible',

        // ✅ Fade/scale IN/OUT
        transformOrigin,
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0px) scale(1)' : `translateY(${enterTranslateY}px) scale(0.98)`,
        transition: `opacity ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`,
        willChange: 'opacity, transform',

        // Klik under fade-out føles mærkeligt -> disable pointer-events under exit
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {showCaret ? renderCaret(anchor, caretOffsetPx) : null}

      <div style={{ borderRadius: 16, overflow: 'hidden' }}>
        <div
          style={{
            //padding: '12px 14px',
            borderBottom: `1px solid ${WOO.sep}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          {/* <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <div style={{ fontWeight: 900, color: WOO.text }}>{title}</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: WOO.sub }}>WooNotes options</div>
          </div> */}

          {/* <button
            type="button"
            onClick={onClose}
            style={{
              border: `2px solid ${WOO.dark}`,
              background: 'rgba(14, 74, 44, 0.06)',
              borderRadius: 12,
              padding: '6px 10px',
              cursor: 'pointer',
              fontWeight: 900,
              color: WOO.text,
              boxShadow: `0 4px 0 ${WOO.dark}`,
            }}
          >
            ✕
          </button> */}
        </div>

        <div style={{ padding: 8, paddingBottom: 12, display: 'grid', gap: 10 }}>
          {items.map(it => {
            if (it.kind === 'separator') {
              return (
                <div
                  key={it.id}
                  style={{
                    height: 1,
                    background: WOO.sep,
                    margin: '6px 6px',
                  }}
                />
              )
            }
            return <WooMenuButton key={it.id} it={it} onClose={() => onClose?.()} />
          })}
        </div>
      </div>
    </div>
  )
}
